import React, { useRef, useEffect, useState } from 'react';
import { Box, Typography, TextField, Radio, RadioGroup, FormControlLabel, Checkbox, FormGroup } from '@availity/element';
import { useForm, Controller, ControllerRenderProps } from 'react-hook-form';
import Button from '../../Utils/components/Button';
import { FormField, RequiredLabel } from './FormComponents';
import { API_OPTIONS, PERSONAL_EMAIL_DOMAINS, ROLE_OPTIONS } from '../constants';
import joinEarlyPageStyles from '../joinEarlyPageStyles';
import { colors } from '../../../static/constants/theme';
import { typography } from '../../../static/constants/typography';

export interface FormData {
  firstName: string;
  lastName: string;
  companyName: string;
  role: string;
  workEmail: string;
  mcpInterest: string;
  apisOfInterest: string[];
  description: string;
  [key: string]: unknown;
}

interface EarlyAccessFormProps {
  onSubmit: (data: FormData) => Promise<void>;
  isSubmitting: boolean;
  submitError: string | null;
  onClearError: () => void;
}

const EarlyAccessForm = ({ onSubmit, isSubmitting, submitError, onClearError }: EarlyAccessFormProps) => {
  const { form, formRow, fieldContainer, textArea, errorText, errorBox, radioGroup, checkboxGroup, dropdownBox, dropdownMenu, dropdownItem } = joinEarlyPageStyles;
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const roleDropdownRef = useRef<HTMLDivElement>(null);

  const { control, handleSubmit, formState: { errors, isValid } } = useForm<FormData>({
    mode: 'onTouched',
    defaultValues: {
      firstName: '',
      lastName: '',
      companyName: '',
      role: '',
      workEmail: '',
      mcpInterest: 'yes',
      apisOfInterest: [],
      description: ''
    },
  });

  const emailValidation = {
    required: 'Work email is required',
    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Please enter a valid email address' },
    validate: (value: string) => {
      const domain = value.split('@')[1]?.toLowerCase();
      if (PERSONAL_EMAIL_DOMAINS.has(domain)) return 'Please use a business email address';
      return true;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target as Node)) {
        setRoleDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleApiChange = (field: ControllerRenderProps<FormData, 'apisOfInterest'>, value: string, checked: boolean) => {
    if (value === 'all') {
      field.onChange(checked ? ['all'] : []);
    } else {
      const newValue = checked ? [...field.value, value] : field.value.filter((v: string) => v !== value);
      field.onChange(field.value.includes('all') ? newValue.filter((v: string) => v !== 'all') : newValue);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={form}>
      <Typography sx={{ color: colors.text.primary, ...typography.montSemibold24 }}>Getting Started</Typography>

      <Box sx={formRow}>
        <FormField label="First Name" name="firstName" rules={{ required: 'First name is required' }} placeholder="First Name" control={control} errors={errors} />
        <FormField label="Last Name" name="lastName" rules={{ required: 'Last name is required' }} placeholder="Last Name" control={control} errors={errors} />
      </Box>

      <Box sx={formRow}>
        <FormField label="Company Name" name="companyName" rules={{ required: 'Company name is required' }} placeholder="Company Name" control={control} errors={errors} />
        <Box sx={fieldContainer} ref={roleDropdownRef}>
          <RequiredLabel>Your Current Role</RequiredLabel>
          <Controller name="role" control={control} rules={{ required: 'Role is required' }} render={({ field, fieldState }) => (
            <Box sx={{ position: 'relative', marginTop: '2px' }}>
              <Box onClick={() => { setRoleDropdownOpen(!roleDropdownOpen); field.onBlur(); }} sx={dropdownBox(!!(errors.role && fieldState.isTouched))}>
                <span style={{ color: field.value ? colors.text.primary : colors.text.placeholder }}>
                  {field.value ? ROLE_OPTIONS.find(r => r.toLowerCase() === field.value) : 'Select Role'}
                </span>
                <Box sx={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 6L8 10L12 6" stroke={colors.text.placeholder} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Box>
              </Box>
              {roleDropdownOpen && (
                <Box sx={dropdownMenu}>
                  {ROLE_OPTIONS.map(role => (
                    <Box key={role} onClick={() => { field.onChange(role.toLowerCase()); setRoleDropdownOpen(false); }} sx={dropdownItem(field.value === role.toLowerCase())}>
                      {role}
                    </Box>
                  ))}
                </Box>
              )}
              {errors.role && fieldState.isTouched && <Typography sx={errorText}>{errors.role.message}</Typography>}
            </Box>
          )} />
        </Box>
      </Box>

      <FormField label="Work Email" name="workEmail" type="email" placeholder="Enter Email" rules={emailValidation} control={control} errors={errors} onFocus={onClearError} />

      <Box sx={fieldContainer}>
        <RequiredLabel>Are you interested in exploring our MCP feature?</RequiredLabel>
        <Controller name="mcpInterest" control={control} render={({ field }) => (
          <RadioGroup {...field} row sx={radioGroup}>
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
        )} />
      </Box>

      <Box sx={fieldContainer}>
        <RequiredLabel>APIs of Interest</RequiredLabel>
        <Controller
          name="apisOfInterest"
          control={control}
          rules={{ validate: (value) => value.length > 0 || 'Please select at least one API' }}
          render={({ field, fieldState }) => (
            <>
              <FormGroup row sx={checkboxGroup}>
                {API_OPTIONS.map(([value, label]) => (
                  <FormControlLabel
                    key={value}
                    control={<Checkbox checked={field.value.includes('all') || field.value.includes(value)} onChange={(e) => { handleApiChange(field, value, e.target.checked); field.onBlur(); }} />}
                    label={label}
                  />
                ))}
              </FormGroup>
              {errors.apisOfInterest && fieldState.isTouched && <Typography sx={errorText}>{errors.apisOfInterest.message}</Typography>}
            </>
          )}
        />
      </Box>

      <Box sx={fieldContainer}>
        <RequiredLabel>What primary problem are you trying to solve with these APIs?</RequiredLabel>
        <Controller
          name="description"
          control={control}
          rules={{ required: 'This field is required', maxLength: { value: 1000, message: 'Description must be 1000 characters or less' } }}
          render={({ field }) => (
            <>
              <TextField
                {...field}
                onChange={(e) => field.onChange(e.target.value.slice(0, 1000))}
                onBlur={(e) => { field.onBlur(); field.onChange(e.target.value.trim()); }}
                multiline
                rows={4}
                placeholder="Describe the primary problem you're trying to solve"
                sx={textArea}
              />
              {errors.description && <Typography sx={errorText}>{errors.description.message}</Typography>}
            </>
          )}
        />
      </Box>

      {submitError && (
        <Box sx={errorBox}>
          <span>{submitError}</span>
        </Box>
      )}
      <Button type="submit" disabled={!isValid || isSubmitting}>
        {isSubmitting ? 'Submitting...' : 'Submit'}
      </Button>
    </Box>
  );
};

export default EarlyAccessForm;
